import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { facilities } from '../data/facilities';
import { Search, MapPin, Building, ShieldCheck, ArrowRight, Plane } from 'lucide-react';
export function Hospitals() {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<string>('All');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('All');
  const [onlyFreeConsult, setOnlyFreeConsult] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const countries = useMemo(() => ['All', ...Array.from(new Set(facilities.map(f => f.country))).sort()], []);
  const specialties = useMemo(() => ['All', ...Array.from(new Set(facilities.flatMap(f => f.keySpecialties))).sort()], []);
  const filteredFacilities = useMemo(() => {
    return facilities.filter(facility => {
      const matchesCountry = selectedCountry === 'All' || facility.country === selectedCountry;
      const matchesSpecialty = selectedSpecialty === 'All' || facility.keySpecialties.includes(selectedSpecialty);
      const matchesConsult = !onlyFreeConsult || facility.freeOnlineConsultation;
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch = query === '' || facility.name.toLowerCase().includes(query) || facility.city.toLowerCase().includes(query) || facility.keySpecialties.some(s => s.toLowerCase().includes(query));
      return matchesCountry && matchesSpecialty && matchesConsult && matchesSearch;
    });
  }, [selectedCountry, selectedSpecialty, onlyFreeConsult, searchQuery]);
  const clearFilters = () => {
    setSelectedCountry('All');
    setSelectedSpecialty('All');
    setOnlyFreeConsult(false);
    setSearchQuery('');
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-986">
      <Helmet data-id="element-987">
        <title data-id="element-988">
          Partner Hospitals and Clinics | Affordable Medical Tourism
        </title>
        <meta name="description" content="Browse our network of internationally accredited partner hospitals and clinics across the globe. Filter by country, specialty, and online consultation availability." data-id="element-989" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/hospitals" data-id="element-990" />
      </Helmet>

      <AppHeader data-id="element-991" />

      <main className="flex-1 py-20 px-6 md:px-12 max-w-7xl mx-auto w-full" data-id="element-992">
        <div className="text-center mb-12" data-id="element-993">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-994">
            Partner Hospitals and Clinics
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-995">
            We coordinate care exclusively with elite, internationally
            accredited healthcare institutions. Explore our network of
            world-class facilities dedicated to international patient care.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-8 space-y-6" data-id="element-996">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-997">
            <div data-id="element-998">
              <label htmlFor="hospital-country" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-999">
                Country
              </label>
              <select id="hospital-country" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1000">
                {countries.map(country => <option key={country} value={country} data-id="element-1001">
                    {country}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1002">
              <label htmlFor="hospital-specialty" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1003">
                Specialty
              </label>
              <select id="hospital-specialty" value={selectedSpecialty} onChange={e => setSelectedSpecialty(e.target.value)} className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1004">
                {specialties.map(spec => <option key={spec} value={spec} data-id="element-1005">
                    {spec}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1006">
              <label htmlFor="hospital-search" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1007">
                Search
              </label>
              <div className="relative" data-id="element-1008">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1009" />
                <input id="hospital-search" type="text" placeholder="Search facilities or specialties" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1010" />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1011">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-id="element-1012">
              <input type="checkbox" checked={onlyFreeConsult} onChange={e => setOnlyFreeConsult(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500" data-id="element-1013" />
              Offers free online consultation
            </label>
            <p className="text-sm text-slate-500" aria-live="polite" data-id="element-1014">
              Showing {filteredFacilities.length} of {facilities.length}{' '}
              facilities
            </p>
          </div>
        </div>

        {/* Grid */}
        {filteredFacilities.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1015">
            {filteredFacilities.map(facility => <Link key={facility.slug} to={`/hospitals/${facility.slug}`} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-md transition-shadow flex flex-col group focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1016">
                <div className="relative h-48 overflow-hidden" data-id="element-1017">
                  <img src={facility.image} alt={`${facility.name} in ${facility.city}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1018" />
                  <div className="absolute top-4 right-4 flex flex-col items-end gap-2" data-id="element-1019">
                    {facility.accreditations.map(acc => <Badge key={acc} className="bg-white text-blue-700 hover:bg-white border-none shadow-sm" data-id="element-1020">
                        <ShieldCheck className="w-3 h-3 mr-1" data-id="element-1021" />
                        {acc}
                      </Badge>)}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col" data-id="element-1022">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2" data-id="element-1023">
                    <MapPin className="w-4 h-4" data-id="element-1024" />
                    {facility.city}, {facility.country}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-700 transition-colors" data-id="element-1025">
                    {facility.name}
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-4" data-id="element-1026">
                    {facility.keySpecialties.slice(0, 3).map(spec => <Badge key={spec} variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100" data-id="element-1027">
                        {spec}
                      </Badge>)}
                    {facility.keySpecialties.length > 3 && <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100" data-id="element-1028">
                        +{facility.keySpecialties.length - 3} more
                      </Badge>}
                  </div>
                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-1029">
                    <div className="flex items-center gap-1 text-sm text-slate-600" data-id="element-1030">
                      <Building className="w-4 h-4" data-id="element-1031" />
                      {facility.totalBeds} Beds
                    </div>
                    <span className="text-blue-700 font-medium flex items-center gap-1 text-sm group-hover:translate-x-1 transition-transform" data-id="element-1032">
                      View details <ArrowRight className="w-4 h-4" data-id="element-1033" />
                    </span>
                  </div>
                </div>
              </Link>)}
          </div> : <div className="text-center py-20" data-id="element-1034">
            <p className="text-xl text-slate-600 mb-4" data-id="element-1035">
              No facilities found matching your criteria.
            </p>
            <Button variant="outline" onClick={clearFilters} data-id="element-1036">
              Clear filters
            </Button>
          </div>}

        {/* Conversion band */}
        <section className="mt-20 bg-blue-700 rounded-3xl px-8 py-14 md:px-16 text-center" data-id="element-1037">
          <Plane className="w-10 h-10 text-blue-200 mx-auto mb-6" data-id="element-1038" />
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1039">
            Not sure which facility is right for you?
          </h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-1040">
            Tell us your procedure and preferences. A care specialist will match
            you with accredited facilities and build a personalized,
            no-obligation plan. There is no cost to ask.
          </p>
          <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50" onClick={() => navigate('/contact')} data-id="element-1041">
            Get your free quote
          </Button>
        </section>
      </main>

      <AppFooter data-id="element-1042" />
    </div>;
}